/**
 * useChartTheme
 *
 * Bridges the app's `useTheme()` context to the ECharts theme registry.
 * Returns the registered theme name to pass to `<ReactECharts theme={…}>`.
 * Invalidates + re-registers the theme whenever the resolved appearance
 * (light/dark) or variant (default/neon) changes.
 */

import { useMemo, useRef } from 'react';
import { useTheme } from '@/store/theme-context';
import { registerChartTheme, invalidateChartThemeCache } from './theme';

export function useChartTheme(): { themeName: string } {
  const { resolvedTheme, variant } = useTheme();
  const prevKey = useRef('');

  const themeName = useMemo(() => {
    const key = `${resolvedTheme}-${variant}`;
    if (key !== prevKey.current) {
      invalidateChartThemeCache();
      prevKey.current = key;
    }
    return registerChartTheme(resolvedTheme, variant);
  }, [resolvedTheme, variant]);

  return { themeName };
}
